import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Modal, Spin } from 'antd';
import {
  COPY_TOOL_HOLD_Z_INDEX,
  TOAST_CONTAINER_SELECTOR,
} from 'constants/appConstants';
import { COPY_TOOL_COPYING_INTO_PLAN_LABEL } from 'constants/uiConstants';

import {
  getIsCopyingIntoInput,
  getIsCopyToolOpen,
} from '../../redux/selectors/CopyToolSelectors';
import { getSelectedInputId } from '../../redux/selectors/InputSelectors';
import { RootState } from '../../redux/Store';

/**
 * Holds Planning while a running copy still has to write the open plan, so
 * nothing can be edited that the copy would overwrite or drop: a modal that
 * can't be closed, above Planning's own modals and dropdowns, with focus moved
 * out of any plan field and Planning's page-wide shortcuts held. Mounted in
 * the workspace, so it never covers another page; it holds every workspace
 * tab, as Insights edits the plan too. The copy tool holds on its own while
 * it's open.
 */
const CopyHoldModal: React.FC = () => {
  const selectedInputId = useSelector(getSelectedInputId);
  const isHeld = useSelector((state: RootState) =>
    getIsCopyingIntoInput(state, selectedInputId)
  );
  const isCopyToolOpen = useSelector(getIsCopyToolOpen);
  const isShown = isHeld && !isCopyToolOpen;

  useEffect(() => {
    if (!isShown) return undefined;
    // A field that had focus would still take typing under the modal.
    const focused = document.activeElement;
    if (focused instanceof HTMLElement) focused.blur();
    // Planning's shortcuts (undo, the task grid's) listen on the document, so a modal alone doesn't stop them.
    const holdKeys = (event: KeyboardEvent) => {
      const { target } = event;
      // The copy's toast keeps its keys (close, Retry); everything else waits.
      if (target instanceof Element && target.closest(TOAST_CONTAINER_SELECTOR))
        return;
      event.stopPropagation();
    };
    window.addEventListener('keydown', holdKeys, true);
    return () => window.removeEventListener('keydown', holdKeys, true);
  }, [isShown]);

  return (
    <Modal
      open={isShown}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      footer={null}
      centered
      width="24rem"
      zIndex={COPY_TOOL_HOLD_Z_INDEX}
    >
      <div role="status" className="flex items-center gap-3 py-2">
        <Spin />
        <p className="text-sm text-gray-dark">
          {COPY_TOOL_COPYING_INTO_PLAN_LABEL}
        </p>
      </div>
    </Modal>
  );
};

export default CopyHoldModal;
